import React, { useMemo } from "react";
import { Button, Tooltip } from "antd";
import { ReloadOutlined, MessageOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import {
  formatDistanceToNow,
  isToday,
  isYesterday,
  differenceInCalendarDays,
  format,
} from "date-fns";
import { askAiTokens as t } from "./tokens";

interface SessionItem {
  session_id: string;
  title?: string;
  created_at?: string;
  updated_at?: string;
}

interface Props {
  sessions: SessionItem[] | null;
  currentSessionId: string | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onSelect: (session: SessionItem) => void;
}

type BucketKey = "today" | "yesterday" | "week" | "month" | "older";
const BUCKET_ORDER: BucketKey[] = [
  "today",
  "yesterday",
  "week",
  "month",
  "older",
];
const BUCKET_LABEL: Record<BucketKey, string> = {
  today: "Today",
  yesterday: "Yesterday",
  week: "Last 7 days",
  month: "Last 30 days",
  older: "Earlier",
};

function bucketFor(iso?: string): BucketKey {
  if (!iso) return "older";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "older";
  if (isToday(d)) return "today";
  if (isYesterday(d)) return "yesterday";
  const diff = differenceInCalendarDays(new Date(), d);
  if (diff <= 7) return "week";
  if (diff <= 30) return "month";
  return "older";
}

function relativeLabel(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  if (isToday(d) || isYesterday(d)) {
    return formatDistanceToNow(d, { addSuffix: true });
  }
  return format(d, "MMM d, yyyy");
}

const SessionRow: React.FC<{
  session: SessionItem;
  isCurrent: boolean;
  onSelect: (s: SessionItem) => void;
  index: number;
}> = ({ session, isCurrent, onSelect, index }) => {
  const updated = session.updated_at || session.created_at;
  const fullTimestamp = updated
    ? format(new Date(updated), "PPpp")
    : undefined;
  return (
    <motion.button
      type="button"
      onClick={() => onSelect(session)}
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        duration: 0.22,
        delay: Math.min(index * 0.02, 0.2),
        ease: [0.22, 1, 0.36, 1],
      }}
      className="askai-history-row"
      data-current={isCurrent ? "true" : "false"}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "10px 12px",
        borderRadius: 8,
        cursor: "pointer",
        border: "none",
        background: isCurrent ? t.color.accentSoft : "transparent",
        fontFamily: "inherit",
        color: "inherit",
        position: "relative",
        marginBottom: 2,
        transition: "background 0.15s ease",
      }}
    >
      {isCurrent && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            left: 0,
            top: 8,
            bottom: 8,
            width: 3,
            borderRadius: 2,
            background: t.color.accent,
          }}
        />
      )}
      <div
        style={{
          fontSize: 13,
          fontWeight: isCurrent ? 600 : 500,
          color: isCurrent ? t.color.accent : t.color.text,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          letterSpacing: -0.1,
        }}
      >
        {session.title?.trim() || "New conversation"}
      </div>
      <Tooltip title={fullTimestamp} mouseEnterDelay={0.4}>
        <div
          style={{
            fontSize: 11.5,
            color: t.color.textSubtle,
            marginTop: 2,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {relativeLabel(updated)}
        </div>
      </Tooltip>
    </motion.button>
  );
};

const SectionLabel: React.FC<{ label: string }> = ({ label }) => (
  <div
    style={{
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: 1.2,
      textTransform: "uppercase",
      color: t.color.textSubtle,
      padding: "10px 12px 6px",
    }}
  >
    {label}
  </div>
);

const HistoryPanel: React.FC<Props> = ({
  sessions,
  currentSessionId,
  loading,
  error,
  onRefresh,
  onSelect,
}) => {
  const buckets = useMemo(() => {
    const out: Record<BucketKey, SessionItem[]> = {
      today: [],
      yesterday: [],
      week: [],
      month: [],
      older: [],
    };
    (sessions || []).forEach((s) => {
      out[bucketFor(s.updated_at || s.created_at)].push(s);
    });
    return out;
  }, [sessions]);

  const totalCount = sessions?.length ?? 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 14px 10px",
          borderBottom: `1px solid ${t.color.border}`,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: t.color.text,
              letterSpacing: -0.1,
            }}
          >
            Chat history
          </span>
          {totalCount > 0 && (
            <span
              style={{
                fontSize: 11,
                color: t.color.textSubtle,
                marginTop: 1,
              }}
            >
              {totalCount} conversation{totalCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <Tooltip title="Refresh">
          <Button
            type="text"
            size="small"
            loading={loading}
            onClick={onRefresh}
            icon={<ReloadOutlined />}
          />
        </Tooltip>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "4px 6px 10px" }}>
        {loading && sessions === null ? (
          <PanelEmpty subtle>Loading conversations…</PanelEmpty>
        ) : error ? (
          <PanelEmpty tone="danger">{error}</PanelEmpty>
        ) : totalCount === 0 ? (
          <PanelEmpty icon>
            Your conversations about this tender will appear here.
          </PanelEmpty>
        ) : (
          (() => {
            let rendered = 0;
            return BUCKET_ORDER.map((key) => {
              const list = buckets[key];
              if (list.length === 0) return null;
              return (
                <div key={key} style={{ marginBottom: 4 }}>
                  <SectionLabel label={BUCKET_LABEL[key]} />
                  {list.map((s) => {
                    const node = (
                      <SessionRow
                        key={s.session_id}
                        session={s}
                        isCurrent={s.session_id === currentSessionId}
                        onSelect={onSelect}
                        index={rendered}
                      />
                    );
                    rendered += 1;
                    return node;
                  })}
                </div>
              );
            });
          })()
        )}
      </div>
    </div>
  );
};

const PanelEmpty: React.FC<{
  children: React.ReactNode;
  subtle?: boolean;
  tone?: "danger";
  icon?: boolean;
}> = ({ children, tone, icon }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 10,
      padding: "44px 18px",
      textAlign: "center",
      color: tone === "danger" ? t.color.danger : t.color.textSubtle,
      fontSize: 12.5,
      lineHeight: 1.55,
    }}
  >
    {icon && (
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          background: t.color.surfaceMuted,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: t.color.textMuted,
          fontSize: 18,
        }}
      >
        <MessageOutlined />
      </div>
    )}
    <div>{children}</div>
  </div>
);

export default HistoryPanel;
